import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Sparkles,
  Sun,
  Heart,
  ShoppingBag,
  GraduationCap,
  MessageCircle,
  ChevronDown,
  Clock,
} from "lucide-react";
import { trackCustomEvent } from "../utils/metaPixel";

const ASTROLOGERS_URL =
  "https://bhavanaastro.onrender.com/api/Consult-astrologers?limit=20";

// ---------- helpers ----------
const getStoredUser = () => {
  try {
    return (
      JSON.parse(sessionStorage.getItem("user")) ||
      JSON.parse(localStorage.getItem("user")) ||
      null
    );
  } catch (e) {
    return null;
  }
};

// true on tablets/desktops (md breakpoint = 768px)
function useIsDesktop() {
  const query = "(min-width: 768px)";
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isDesktop;
}

// ---------- content ----------
const quickServices = [
  { title: "Free Kundli", desc: "Janam kundli turant", icon: Sparkles, link: "/free-kundali" },
  { title: "Daily Horoscope", desc: "Aaj ka rashifal", icon: Sun, link: "/horoscopes" },
  { title: "Matchmaking", desc: "Kundli milan", icon: Heart, link: "/match-making" },
  { title: "Shop", desc: "Rudraksha aur gemstones", icon: ShoppingBag, link: "/shopping" },
  { title: "Courses", desc: "Astrology aur healing", icon: GraduationCap, link: "/occult" },
];

const cardShadow =
  "shadow-[0_1px_2px_rgba(23,20,63,0.06),0_10px_24px_-14px_rgba(23,20,63,0.25)]";

const steps = [
  "Sign up karo aur birth details daalo",
  "Apna AI astrologer chuno",
  "Sawaal pucho. Pehla 1 minute free hai",
];

const faqs = [
  {
    q: "Kya yeh asli insaan hai?",
    a: "Nahi. Aap AI astrologer se chat karte hain. Jawab ko margdarshan ki tarah lein, pakki bhavishyavani ki tarah nahi.",
  },
  {
    q: "Kya chat sach mein free hai?",
    a: "Haan, naye user ko 1 minute free milta hai. Uske baad chat jaari rakhne ke liye wallet mein balance chahiye.",
  },
  {
    q: "Meri details private rehti hain?",
    a: (
      <>
        Haan, aapki details private aur confidential rakhi jaati hain. Poori jaankari{" "}
        <Link to="/privacy-policy" className="font-semibold text-[#3A2E9C] underline">
          Privacy Policy
        </Link>{" "}
        mein hai.
      </>
    ),
  },
];

// Styles for the home page (fonts are loaded in public/index.html)
const mobileCss = `
  .as-home { font-family: 'Hind', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; }
  .as-display { font-family: 'Baloo 2', 'Hind', system-ui, sans-serif; }
  .as-sky {
    background:
      radial-gradient(1.5px 1.5px at 12% 16%, rgba(255,255,255,.7), transparent 60%),
      radial-gradient(1px 1px at 78% 10%, rgba(255,255,255,.6), transparent 60%),
      radial-gradient(1.5px 1.5px at 90% 44%, rgba(255,255,255,.5), transparent 60%),
      radial-gradient(1px 1px at 28% 62%, rgba(255,255,255,.5), transparent 60%),
      radial-gradient(1px 1px at 66% 78%, rgba(255,255,255,.45), transparent 60%),
      radial-gradient(120% 60% at 90% 0%, rgba(255,182,39,.20), transparent 60%),
      #17143F;
  }
  .as-faq summary { list-style: none; }
  .as-faq summary::-webkit-details-marker { display: none; }

  /* One sequence on load: question appears, AI "types", AI answers */
  @keyframes as-pop {
    from { opacity: 0; transform: translateY(8px) scale(.98); }
    to   { opacity: 1; transform: none; }
  }
  @keyframes as-typing {
    0%   { opacity: 0; }
    10%, 88% { opacity: 1; }
    100% { opacity: 0; visibility: hidden; }
  }
  @keyframes as-dot {
    0%, 80%, 100% { transform: translateY(0); opacity: .45; }
    40% { transform: translateY(-3px); opacity: 1; }
  }
  .as-b1     { animation: as-pop .45s .5s both; }
  .as-typing { animation: as-typing 1.5s 1.2s both; }
  .as-reply  { animation: as-pop .45s 2.7s both; }
  .as-dot    { animation: as-dot 1s infinite; }
  .as-dot:nth-child(2) { animation-delay: .15s; }
  .as-dot:nth-child(3) { animation-delay: .3s; }
  @media (prefers-reduced-motion: reduce) {
    .as-b1, .as-reply { animation: none; }
    .as-typing { display: none; }
    .as-dot { animation: none; }
  }
`;

export default function Home() {
  const isDesktop = useIsDesktop();

  const [aiAstrologers, setAiAstrologers] = useState([]);
  const [loadingAstros, setLoadingAstros] = useState(true);

  // Sticky bottom button (mobile)
  const heroCtaRef = useRef(null);
  const endRef = useRef(null);
  const [heroOut, setHeroOut] = useState(false);
  const [atEnd, setAtEnd] = useState(false);

  // Where the "chat" buttons go: logged-in users go straight to the chat list,
  // everyone else goes to sign up.
  const storedUser = getStoredUser();
  const chatLink = storedUser?.role === "user" ? "/ai-consultancy" : "/user/register";
  const track = (placement) => trackCustomEvent("FreeChatCTA", { placement });

  // Fetch astrologers (longer timeout + one retry: the server can wake up slowly)
  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 1) => {
      try {
        const res = await axios.get(ASTROLOGERS_URL, { timeout: 30000 });
        if (cancelled) return;
        const all = res.data.astrologers || [];
        setAiAstrologers(all.filter((a) => a.isAI));
        setLoadingAstros(false);
      } catch (error) {
        console.error("Error fetching astrologers:", error);
        if (!cancelled && attempt < 2) return load(attempt + 1);
        if (!cancelled) setLoadingAstros(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Prevent back button exit (existing behaviour)
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Mobile: show sticky button once the hero button scrolls away, hide near the end
  useEffect(() => {
    if (isDesktop || typeof IntersectionObserver === "undefined") return;
    const heroEl = heroCtaRef.current;
    const endEl = endRef.current;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.target === heroEl) setHeroOut(!e.isIntersecting);
        if (e.target === endEl) setAtEnd(e.isIntersecting);
      });
    });
    if (heroEl) obs.observe(heroEl);
    if (endEl) obs.observe(endEl);
    return () => obs.disconnect();
  }, [isDesktop]);

  // =====================================================================
  // DESKTOP
  // =====================================================================
  if (isDesktop) {
    return (
      <div className="as-home min-h-screen bg-[#F5F4FB] text-slate-800">
        <style>{mobileCss}</style>

        {/* ---------- 1. Hero: the ad's promise, one button, a live chat sample ---------- */}
        <section className="as-sky relative overflow-hidden text-white">
          <div className="mx-auto grid max-w-6xl items-center gap-12 px-8 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:py-20">
            <div>
              <h1 className="as-display text-[52px] font-extrabold leading-[1.05] lg:text-[60px]">
                Dadi pooch rahi hain: shaadi kab hogi?
              </h1>
              <p className="mt-4 max-w-[34ch] text-xl leading-snug text-indigo-100">
                Janam tithi batao aur AI astrologer se seedha pooch lo.
              </p>

              <Link
                to={chatLink}
                onClick={() => track("hero")}
                className="as-display mt-8 inline-flex items-center gap-3 rounded-2xl bg-[#FFB627] px-9 py-4 text-[22px] font-bold text-[#1E1A4D] shadow-[0_12px_32px_-8px_rgba(255,182,39,0.6)] transition hover:bg-[#FFC24D] active:scale-[0.98]"
              >
                <MessageCircle size={24} strokeWidth={2.4} />
                Start free chat
              </Link>

              <p className="mt-4 max-w-md text-sm leading-snug text-indigo-200">
                Pehla 1 minute free, uske baad wallet se. Yeh AI astrologer hai, insaan nahi. Aapki details private rehti hain.
              </p>

              {!storedUser && (
                <p className="mt-2 text-[15px] text-indigo-100">
                  Pehle se account hai?{" "}
                  <Link to="/login?next=/ai-consultancy" className="font-semibold text-[#FFB627] underline underline-offset-2">
                    Login karo
                  </Link>
                </p>
              )}
            </div>

            {/* Sample chat window (an example, not a real prediction) */}
            <div className="mx-auto w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-white/[0.06] shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]">
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5">
                <div className="flex items-center gap-2.5">
                  <span className="as-display grid h-9 w-9 place-items-center rounded-full bg-[#3A2E9C] text-[13px] font-bold text-white">
                    AI
                  </span>
                  <div>
                    <p className="as-display text-[16px] font-bold leading-tight">AI Astrologer</p>
                    <p className="text-xs text-indigo-200">Chat kuch aisi dikhti hai</p>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-[#FFB627]">
                  <Clock size={13} /> 1 min free
                </span>
              </div>

              <div className="px-5 py-6">
                <div className="as-b1 flex justify-end">
                  <div className="max-w-[78%] rounded-2xl rounded-br-md bg-[#FFB627] px-4 py-2.5 text-[17px] font-medium text-[#1E1A4D]">
                    Meri shaadi kab hogi?
                  </div>
                </div>

                <div className="mt-3 grid">
                  <div className="as-typing flex items-end [grid-area:1/1]">
                    <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md bg-white px-4 py-3.5">
                      <span className="as-dot h-2 w-2 rounded-full bg-slate-400" />
                      <span className="as-dot h-2 w-2 rounded-full bg-slate-400" />
                      <span className="as-dot h-2 w-2 rounded-full bg-slate-400" />
                    </div>
                  </div>
                  <div className="as-reply flex items-end gap-2.5 [grid-area:1/1]">
                    <span className="as-display grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#3A2E9C] text-[12px] font-bold text-white">
                      AI
                    </span>
                    <div className="max-w-[84%] rounded-2xl rounded-bl-md bg-white px-4 py-2.5 text-[17px] leading-snug text-slate-800">
                      Pehle apni janam tithi, samay aur jagah batao. Phir kundli dekhkar main guidance deta hoon.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------- 2. AI astrologers ---------- */}
        <section className="mx-auto max-w-6xl px-8 pt-16">
          <h2 className="as-display text-[32px] font-bold leading-tight text-[#17143F]">
            Apna AI astrologer chuno
          </h2>
          <p className="mt-1 text-base text-slate-500">Kisi par bhi click karo, free chat shuru.</p>

          <div className="mt-6 grid gap-4 md:grid-cols-3 lg:grid-cols-4">
            {loadingAstros &&
              [0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`animate-pulse rounded-2xl bg-white p-5 ${cardShadow}`}
                >
                  <div className="mx-auto h-20 w-20 rounded-full bg-slate-200" />
                  <div className="mx-auto mt-4 h-4 w-32 rounded bg-slate-200" />
                  <div className="mx-auto mt-2 h-3 w-40 rounded bg-slate-100" />
                  <div className="mt-5 h-10 rounded-full bg-slate-200" />
                </div>
              ))}

            {!loadingAstros &&
              aiAstrologers.map((astro) => (
                <Link
                  key={astro._id}
                  to={chatLink}
                  onClick={() => track("astrologer_card")}
                  className={`group flex flex-col items-center rounded-2xl border border-transparent bg-white p-5 text-center transition hover:border-[#C9C4E8] ${cardShadow}`}
                >
                  <div className="relative h-20 w-20">
                    <img
                      src={astro.photo || "/ai.png"}
                      alt={astro.name}
                      loading="lazy"
                      className="h-20 w-20 rounded-full object-cover"
                    />
                    <span className="as-display absolute -bottom-1 -right-1 rounded-full border-2 border-white bg-[#3A2E9C] px-2 text-[11px] font-bold leading-5 text-white">
                      AI
                    </span>
                  </div>
                  <h3 className="mt-3 w-full truncate text-[18px] font-semibold text-[#17143F]">
                    {astro.name}
                  </h3>
                  <p className="mt-0.5 w-full truncate text-sm text-slate-500">
                    {astro.systemsKnown?.slice(0, 3).join(", ") || "AI astrological guidance"}
                  </p>
                  <p className="w-full truncate text-sm text-slate-400">
                    {astro.languagesKnown?.join(", ")}
                  </p>
                  <span className="mt-4 block w-full rounded-full bg-[#3A2E9C] py-2.5 text-sm font-semibold text-white transition group-hover:bg-[#2F2582]">
                    Chat free
                  </span>
                </Link>
              ))}

            {!loadingAstros && aiAstrologers.length === 0 && (
              <div className={`col-span-full rounded-2xl bg-white p-8 text-center ${cardShadow}`}>
                <p className="text-slate-600">
                  Astrologers load hone mein thoda time lag raha hai. Aap phir bhi chat shuru kar sakte hain.
                </p>
                <Link
                  to={chatLink}
                  onClick={() => track("astrologer_fallback")}
                  className="mt-4 inline-block rounded-full bg-[#3A2E9C] px-7 py-3 text-sm font-semibold text-white"
                >
                  Start free chat
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* ---------- 3. How it works (a real sequence) ---------- */}
        <section className="mx-auto max-w-6xl px-8 pt-20">
          <h2 className="as-display text-[32px] font-bold leading-tight text-[#17143F]">
            Kaise kaam karta hai
          </h2>
          <ol className="relative mt-8 grid gap-8 md:grid-cols-3">
            <span
              className="absolute left-[16.66%] right-[16.66%] top-[18px] hidden h-px bg-[#C9C4E8] md:block"
              aria-hidden="true"
            />
            {steps.map((text, i) => (
              <li key={i} className="relative flex flex-col items-center text-center">
                <span className="as-display relative z-10 grid h-9 w-9 place-items-center rounded-full bg-[#17143F] text-[16px] font-bold text-[#FFB627]">
                  {i + 1}
                </span>
                <span className="mt-3 max-w-[22ch] text-[17px] font-medium text-slate-700">{text}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* ---------- 4. Other services (quiet, secondary) ---------- */}
        <section className="mx-auto max-w-6xl px-8 pt-20">
          <h2 className="as-display text-[32px] font-bold leading-tight text-[#17143F]">
            Aur bhi services
          </h2>
          <div className="mt-6 grid gap-3 md:grid-cols-3 lg:grid-cols-5">
            {quickServices.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.title}
                  to={s.link}
                  className="flex items-center gap-3 rounded-2xl border border-[#DAD6F0] bg-white p-4 transition hover:border-[#3A2E9C]"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#EEEBFA] text-[#3A2E9C]">
                    <Icon size={20} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[15px] font-semibold text-[#17143F]">{s.title}</span>
                    <span className="block truncate text-[13px] text-slate-500">{s.desc}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* ---------- 5. FAQ ---------- */}
        <section className="mx-auto grid max-w-6xl gap-8 px-8 pt-20 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14">
          <h2 className="as-display text-[32px] font-bold leading-tight text-[#17143F]">
            Aksar pooche jaane wale sawaal
          </h2>
          <div
            className={`as-faq divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white ${cardShadow}`}
          >
            {faqs.map((f) => (
              <details key={f.q} className="group px-6 py-4">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-[17px] font-semibold text-[#17143F]">
                  {f.q}
                  <ChevronDown
                    size={20}
                    className="shrink-0 text-slate-400 transition-transform group-open:rotate-180"
                  />
                </summary>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ---------- Closing call to action ---------- */}
        <section className="as-sky mt-20 text-white">
          <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 px-8 py-14 text-center md:flex-row md:justify-between md:text-left">
            <div>
              <h2 className="as-display text-[32px] font-extrabold leading-tight">
                Shaadi ka sawaal ab pooch lo
              </h2>
              <p className="mt-1 text-base text-indigo-200">
                Pehla 1 minute free, uske baad wallet se.
              </p>
            </div>
            <Link
              to={chatLink}
              onClick={() => track("footer_band")}
              className="as-display inline-flex shrink-0 items-center gap-3 rounded-2xl bg-[#FFB627] px-9 py-4 text-[22px] font-bold text-[#1E1A4D] shadow-[0_12px_32px_-8px_rgba(255,182,39,0.6)] transition hover:bg-[#FFC24D] active:scale-[0.98]"
            >
              <MessageCircle size={24} strokeWidth={2.4} />
              Start free chat
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // =====================================================================
  // MOBILE
  // =====================================================================
  const showSticky = heroOut && !atEnd;

  return (
    <div className="as-home min-h-screen bg-[#F5F4FB] text-slate-800">
      <style>{mobileCss}</style>

      {/* ---------- 1. Hero: the ad's promise, one button, a live chat sample ---------- */}
      <section className="as-sky relative overflow-hidden px-5 pb-8 pt-6 text-white">
        <h1 className="as-display text-[31px] font-extrabold leading-[1.1]">
          Dadi pooch rahi hain: shaadi kab hogi?
        </h1>
        <p className="mt-2 max-w-[30ch] text-[16px] leading-snug text-indigo-100">
          Janam tithi batao aur AI astrologer se seedha pooch lo.
        </p>

        <Link
          ref={heroCtaRef}
          to={chatLink}
          onClick={() => track("hero")}
          className="as-display mt-5 flex w-full items-center justify-center gap-2.5 rounded-2xl bg-[#FFB627] px-6 py-3.5 text-[19px] font-bold text-[#1E1A4D] shadow-[0_10px_28px_-8px_rgba(255,182,39,0.65)] transition active:scale-[0.98]"
        >
          <MessageCircle size={22} strokeWidth={2.4} />
          Start free chat
        </Link>

        <p className="mt-3 text-[13px] leading-snug text-indigo-200">
          Pehla 1 minute free, uske baad wallet se. Yeh AI astrologer hai, insaan nahi. Aapki details private rehti hain.
        </p>

        {!storedUser && (
          <p className="mt-2 text-[14px] text-indigo-100">
            Pehle se account hai?{" "}
            <Link to="/login?next=/ai-consultancy" className="font-semibold text-[#FFB627] underline underline-offset-2">
              Login karo
            </Link>
          </p>
        )}

        {/* Sample chat (an example, not a real prediction) */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.06] p-3.5">
          <p className="mb-3 text-xs text-indigo-200">Chat kuch aisi dikhti hai</p>

          <div className="as-b1 flex justify-end">
            <div className="max-w-[78%] rounded-2xl rounded-br-md bg-[#FFB627] px-3.5 py-2 text-[15px] font-medium text-[#1E1A4D]">
              Meri shaadi kab hogi?
            </div>
          </div>

          <div className="mt-2.5 grid">
            <div className="as-typing flex items-end [grid-area:1/1]">
              <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md bg-white px-4 py-3.5">
                <span className="as-dot h-1.5 w-1.5 rounded-full bg-slate-400" />
                <span className="as-dot h-1.5 w-1.5 rounded-full bg-slate-400" />
                <span className="as-dot h-1.5 w-1.5 rounded-full bg-slate-400" />
              </div>
            </div>
            <div className="as-reply flex items-end gap-2 [grid-area:1/1]">
              <span className="as-display grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#3A2E9C] text-[11px] font-bold text-white">
                AI
              </span>
              <div className="max-w-[82%] rounded-2xl rounded-bl-md bg-white px-3.5 py-2 text-[15px] leading-snug text-slate-800">
                Pehle apni janam tithi, samay aur jagah batao. Phir kundli dekhkar main guidance deta hoon.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 2. AI astrologers: a tappable list ---------- */}
      <section className="px-4 pt-8">
        <h2 className="as-display text-[22px] font-bold leading-tight text-[#17143F]">
          Apna AI astrologer chuno
        </h2>
        <p className="mt-0.5 text-sm text-slate-500">Kisi par bhi tap karo, free chat shuru.</p>

        <div
          className={`mt-3 divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white ${cardShadow}`}
        >
          {loadingAstros &&
            [0, 1, 2].map((i) => (
              <div key={i} className="flex animate-pulse items-center gap-3 px-3.5 py-3">
                <div className="h-14 w-14 shrink-0 rounded-full bg-slate-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 rounded bg-slate-200" />
                  <div className="h-3 w-44 rounded bg-slate-100" />
                </div>
                <div className="h-9 w-20 rounded-full bg-slate-200" />
              </div>
            ))}

          {!loadingAstros &&
            aiAstrologers.map((astro) => (
              <Link
                key={astro._id}
                to={chatLink}
                onClick={() => track("astrologer_row")}
                className="flex items-center gap-3 px-3.5 py-3 transition active:bg-slate-50"
              >
                <div className="relative h-14 w-14 shrink-0">
                  <img
                    src={astro.photo || "/ai.png"}
                    alt={astro.name}
                    loading="lazy"
                    className="h-14 w-14 rounded-full object-cover"
                  />
                  <span className="as-display absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-white bg-[#3A2E9C] px-1.5 text-[10px] font-bold leading-4 text-white">
                    AI
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-[16px] font-semibold text-[#17143F]">{astro.name}</h3>
                  <p className="truncate text-[13px] text-slate-500">
                    {astro.systemsKnown?.slice(0, 3).join(", ") || "AI astrological guidance"}
                  </p>
                  <p className="truncate text-[13px] text-slate-400">
                    {astro.languagesKnown?.join(", ")}
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-[#3A2E9C] px-3.5 py-2 text-[13px] font-semibold text-white">
                  Chat free
                </span>
              </Link>
            ))}

          {!loadingAstros && aiAstrologers.length === 0 && (
            <div className="p-5 text-center">
              <p className="text-sm text-slate-600">
                Astrologers load hone mein thoda time lag raha hai. Aap phir bhi chat shuru kar sakte hain.
              </p>
              <Link
                to={chatLink}
                onClick={() => track("astrologer_fallback")}
                className="mt-3 inline-block rounded-full bg-[#3A2E9C] px-6 py-2.5 text-sm font-semibold text-white"
              >
                Start free chat
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ---------- 3. How it works (a real sequence) ---------- */}
      <section className="px-4 pt-9">
        <h2 className="as-display text-[22px] font-bold leading-tight text-[#17143F]">
          Kaise kaam karta hai
        </h2>
        <ol className="relative mt-4 space-y-4">
          <span className="absolute bottom-4 left-4 top-4 w-px bg-[#C9C4E8]" aria-hidden="true" />
          {steps.map((text, i) => (
            <li key={i} className="relative flex items-center gap-3.5">
              <span className="as-display relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#17143F] text-[15px] font-bold text-[#FFB627]">
                {i + 1}
              </span>
              <span className="text-[15px] font-medium text-slate-700">{text}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* ---------- 4. Other services (quiet, secondary) ---------- */}
      <section className="px-4 pt-9">
        <h2 className="as-display text-[22px] font-bold leading-tight text-[#17143F]">
          Aur bhi services
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {quickServices.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.title}
                to={s.link}
                className="flex items-center gap-2 rounded-full border border-[#DAD6F0] bg-white px-3.5 py-2 text-[14px] font-medium text-[#17143F] transition active:bg-[#EEEBFA]"
              >
                <Icon size={16} className="text-[#3A2E9C]" />
                {s.title}
              </Link>
            );
          })}
        </div>
      </section>

      {/* ---------- 5. FAQ ---------- */}
      <section className="px-4 pb-12 pt-9">
        <h2 className="as-display text-[22px] font-bold leading-tight text-[#17143F]">
          Aksar pooche jaane wale sawaal
        </h2>
        <div
          className={`as-faq mt-3 divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white ${cardShadow}`}
        >
          {faqs.map((f) => (
            <details key={f.q} className="group px-4 py-3.5">
              <summary className="flex cursor-pointer items-center justify-between gap-3 text-[15px] font-semibold text-[#17143F]">
                {f.q}
                <ChevronDown
                  size={18}
                  className="shrink-0 text-slate-400 transition-transform group-open:rotate-180"
                />
              </summary>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <div ref={endRef} className="h-px" />

      {/* ---------- Sticky button after the hero scrolls away ---------- */}
      {showSticky && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 bg-[#17143F]/95 px-4 pt-3 backdrop-blur"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <Link
            to={chatLink}
            onClick={() => track("sticky")}
            className="as-display flex w-full items-center justify-center gap-2.5 rounded-2xl bg-[#FFB627] py-3 text-[18px] font-bold text-[#1E1A4D] transition active:scale-[0.98]"
          >
            <MessageCircle size={21} strokeWidth={2.4} />
            Start free chat
          </Link>
        </div>
      )}
    </div>
  );
}